import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React from "react";
import { CheckSquare } from "react-feather";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  HasilUjiSilangOptions,
  KetebalanSDOptions,
  KualitasPewarnaanOptions,
  MonthOptions,
  ParasyteOptions,
} from "utils/constant";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import { useEffect } from "react";
import UjiSilangService from "services/UjiSilang";
import { errorToastNotif, successToastNotif } from "utils/common";
import { toast } from "react-toastify";
import InputField from "components/atoms/InputField";
import { maskNumber } from "utils/helpers";

const ujiSilangService = new UjiSilangService();

const Table = () => {
  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
    filter_name: "",
  });
  const [defaultData, setDefaultData] = useState([]);
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { currentUser } = useSelector((state) => state.authReducer);
  const provinceId = currentUser?.province_id;

  const fetchInputData = async () => {
    if (!filterState.filter_month) return;
    setIsLoading(true);
    try {
      const { filter_month, filter_year, filter_name } = filterState;
      const response = await ujiSilangService.slideGetAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy: `province_id,year,month,slide,name,is_uji_silang_prov`,
        searchValue: `${provinceId},${filter_year},${filter_month},positif,${filter_name},Ya`,
      });

      const data = (response.slide || []).map((o) => ({
        ...o,
        isEdit: false,
        saved_tgl_slide_diperiksa_prov: o.tgl_slide_diperiksa_prov || "",
      }));

      setDefaultData(JSON.parse(JSON.stringify(data)));
      setDataState((prevState) => ({
        ...prevState,
        data,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onSaveData = async (rowIndex, item) => {
    let errorMessage = "";
    if (!item.tgl_slide_diperiksa_prov) {
      errorMessage = "Tanggal slide diperiksa harus diisi";
    } else if (!item.ketebalan_sd_tebal_prov) {
      errorMessage = "Ketebalan SD tebal harus diisi";
    } else if (!item.ketebalan_sd_tipis_prov) {
      errorMessage = "Ketebalan SD tipis harus diisi";
    } else if (!item.pewarnaan_prov) {
      errorMessage = "Pewarnaan harus diisi";
    } else if (!item.hasil_prov) {
      errorMessage = "Hasil uji silang harus diisi";
    }

    if (errorMessage) {
      return toast.error(errorMessage, {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    delete item.isEdit;

    await ujiSilangService
      .slideUpdate({ ...item, slide: "positif" }, item.id)
      .then(() => {
        successToastNotif("Data berhasil diperbarui");
        setDataState((prevState) => {
          const newData = [...prevState.data];
          newData[rowIndex].isEdit = false;
          newData[rowIndex].saved_tgl_slide_diperiksa_prov =
            item.tgl_slide_diperiksa_prov;
          return { ...prevState, data: newData };
        });

        if (item.hasil_prov.includes("Negatif")) {
          toast.info("Jika Negatif, silakan hapus data pasien di Regmal 1", {
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            theme: "colored",
            draggable: true,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        errorToastNotif(err);
      });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      size: 100,
      cell: ({ row: { index, original }, table }) => {
        const oldData = defaultData.find((o) => o.id === original.id);
        return (
          <>
            <div className="button-actions">
              {original.isEdit ? (
                <>
                  <ActionButton
                    onClick={() => onSaveData(index, original)}
                    btnType={"save"}
                  />
                  <ActionButton
                    onClick={() => {
                      table.options.meta?.updateData(
                        index,
                        "isEdit",
                        false,
                        JSON.parse(JSON.stringify(oldData))
                      );
                    }}
                    btnType={"cancel"}
                  />
                </>
              ) : (
                <ActionButton
                  onClick={() => {
                    table.options.meta?.updateData(index, "isEdit", true);
                  }}
                  btnType={"edit"}
                />
              )}
            </div>
            {original.saved_tgl_slide_diperiksa_prov && (
              <div
                style={{
                  color: "#198754",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <CheckSquare size={18} />
                Sudah Diperiksa
              </div>
            )}
          </>
        );
      },
    },
    {
      header: "Fasyankes",
      accessorKey: "fasyankes_id",
      cell: ({ row }) =>
        row.original.fasyankes_id
          ? row.original.fasyankes_id?.split("|")[1]
          : "",
      size: 200,
    },
    {
      header: "Nama",
      accessorKey: "name",
      cell: ({ row }) => row.original.name || "",
    },
    {
      header: "Kode slide",
      accessorKey: "slide_code",
      cell: ({ row }) => row.original.slide_code || "",
      size: 250,
    },
    {
      header: "Petugas mikroskopis",
      accessorKey: "petugas_mikroskopis",
      cell: ({ row }) => {
        const value = row.original.petugas_mikroskopis || "";
        const [name, nik] = value.split(" - ");
        return `${name} - ${maskNumber(nik)}`;
      },
      size: 250,
    },
    {
      header: "Tanggal slide dikirim",
      accessorKey: "tgl_slide_dikirim_kabkota",
      cell: ({ row }) => row.original.tgl_slide_dikirim_kabkota || "",
      size: 250,
    },
    {
      header: "Tanggal slide diperiksa",
      accessorKey: "tgl_slide_diperiksa_prov",
      cell: ({ row: { index, original }, table }) => {
        if (!original?.isEdit) {
          return original.tgl_slide_diperiksa_prov || "";
        }
        return (
          <InputDate
            placeholder={"Pilih tanggal"}
            name={"tgl_slide_diperiksa_prov"}
            dateFormat={"YYYY-MM-DD"}
            value={original.tgl_slide_diperiksa_prov ?? ""}
            onChange={(e) => {
              const selectedDate = e ? moment(e).format("YYYY-MM-DD") : "";
              table.options.meta?.updateData(
                index,
                "tgl_slide_diperiksa_prov",
                selectedDate
              );
            }}
          />
        );
      },
      size: 250,
    },
    {
      header: "Ketebalan SD tebal",
      accessorKey: "ketebalan_sd_tebal_prov",
      cell: ({ row: { index, original }, table }) => {
        return (
          <InputSelect
            name={"ketebalan_sd_tebal_prov"}
            placeholder={"Pilih ketebalan SD tebal"}
            data={KetebalanSDOptions}
            disabled={!original?.isEdit}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              table.options.meta?.updateData(
                index,
                "ketebalan_sd_tebal_prov",
                selectedValue
              );
            }}
            value={KetebalanSDOptions.find(
              (o) => o.value === original.ketebalan_sd_tebal_prov
            )}
          />
        );
      },
      size: 250,
    },
    {
      header: "Ketebalan SD tipis",
      accessorKey: "ketebalan_sd_tipis_prov",
      cell: ({ row: { index, original }, table }) => {
        return (
          <InputSelect
            name={"ketebalan_sd_tipis_prov"}
            placeholder={"Pilih ketebalan SD tipis"}
            data={KetebalanSDOptions}
            disabled={!original?.isEdit}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              table.options.meta?.updateData(
                index,
                "ketebalan_sd_tipis_prov",
                selectedValue
              );
            }}
            value={KetebalanSDOptions.find(
              (o) => o.value === original.ketebalan_sd_tipis_prov
            )}
          />
        );
      },
      size: 250,
    },
    {
      header: "Pewarnaan",
      accessorKey: "pewarnaan_prov",
      cell: ({ row: { index, original }, table }) => {
        return (
          <InputSelect
            name={"pewarnaan_prov"}
            placeholder={"Pilih pewarnaan"}
            data={KualitasPewarnaanOptions}
            disabled={!original?.isEdit}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              table.options.meta?.updateData(
                index,
                "pewarnaan_prov",
                selectedValue
              );
            }}
            value={KualitasPewarnaanOptions.find(
              (o) => o.value === original.pewarnaan_prov
            )}
          />
        );
      },
      size: 250,
    },
    {
      header: "Hasil uji silang",
      accessorKey: "hasil_prov",
      cell: ({ row: { index, original }, table }) => {
        const optionOtherMix = ParasyteOptions.filter(
          (o) => o.value !== "P.Mix"
        );
        return (
          <div className="grid grid-cols-1 gap-2">
            <InputSelect
              name={"hasil_prov"}
              placeholder={"Pilih jenis parasit"}
              data={HasilUjiSilangOptions}
              disabled={!original?.isEdit}
              onChange={(e) => {
                const selectedValue = e ? e.value : "";
                table.options.meta?.updateData(
                  index,
                  "hasil_prov",
                  selectedValue
                );
              }}
              value={HasilUjiSilangOptions.find((o) => {
                if (original?.hasil_prov?.includes("Mix")) {
                  return o.value === "P.Mix";
                }
                return o.value === original?.hasil_prov;
              })}
            />
            {original?.hasil_prov?.includes("Mix") && (
              <InputSelect
                isMultipleSelect
                name={"hasil_prov"}
                placeholder={"Pilih jenis parasit"}
                data={optionOtherMix}
                disabled={!original?.isEdit}
                onChange={(e) => {
                  table.options.meta?.updateData(
                    index,
                    "hasil_prov",
                    `P.Mix,${e.map((item) => item.value).join(",")}`
                  );
                }}
                value={optionOtherMix.filter((o) =>
                  original?.hasil_prov?.includes(o.value)
                )}
              />
            )}
          </div>
        );
      },
      size: 250,
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    meta: {
      updateData: (rowIndex, field, value, rowData) => {
        setDataState((prevState) => {
          const newData = [...prevState.data];
          if (rowData) newData[rowIndex] = rowData;
          newData[rowIndex][field] = value;
          return { ...prevState, data: newData };
        });
      },
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">
        Uji Silang Provinsi - Verifikasi Slide Positif
      </h1>
      {/* Filter section start */}
      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                filter_year: selectedYear,
              });
            }}
            value={filterState.filter_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                filter_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month
            )}
          />
        </div>
        <div className="filter-item">
          <InputField
            placeholder="Masukan nama"
            value={filterState.filter_name}
            onChange={(e) => {
              setFilterState({
                ...filterState,
                filter_name: e.target.value,
              });
            }}
            name="filter_name"
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => fetchInputData()}
        >
          Terapkan
        </Button>
      </div>
      {/* Filter section end */}

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
    </div>
  );
};

export default Table;
